import React, { useState, useCallback } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import StatusBadge from '../../components/common/StatusBadge';
import ToastStack, { useToasts } from '../../components/common/Toast';
import { useAuth } from '../../context/AuthContext';
import { getOrderList, updateOrderStatus } from '../../api/order';
import { formatDateTime } from '../../utils/format';

const STATUS_OPTIONS = [
  { value: '', label: '전체 상태' },
  { value: 'PAID', label: '결제완료' },
  { value: 'READY', label: '상품준비중' },
  { value: 'SHIPPING', label: '배송중' },
  { value: 'DELIVERED', label: '배송완료' },
  { value: 'CANCELLED', label: '주문취소' },
  { value: 'REFUNDED', label: '환불완료' },
];

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}
function daysAgoStr(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

export default function OrderListPage() {
  const { auth } = useAuth();
  const [filters, setFilters] = useState({
    searchStartDate: daysAgoStr(7),
    searchEndDate: todayStr(),
    orderStatus: '',
    memberAccount: '',
  });

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasNext, setHasNext] = useState(false);
  const [cursorHistory, setCursorHistory] = useState([null]); // index 0 = 첫 페이지(cursor 없음)
  const [pageIndex, setPageIndex] = useState(0);

  const [statusTarget, setStatusTarget] = useState(null); // { order, newStatus }
  const { toasts, pushToast, removeToast } = useToasts();

  const fetchPage = useCallback(async (cursor, currentFilters) => {
    setLoading(true);
    setError('');
    try {
      const res = await getOrderList({ ...currentFilters, cursor, pageSize: 20 });
      setRows(res.data.list || []);
      setHasNext(res.data.hasNext);
      return res.data;
    } catch (err) {
      setError(err.response?.data?.message || '주문 목록을 불러오지 못했습니다.');
      setRows([]);
      setHasNext(false);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSearch = async (e) => {
    e.preventDefault();
    setCursorHistory([null]);
    setPageIndex(0);
    await fetchPage(null, filters);
  };

  const handleNext = async () => {
    if (!hasNext || rows.length === 0) return;
    const lastOrderId = rows[rows.length - 1].orderId;
    const nextHistory = [...cursorHistory.slice(0, pageIndex + 1), lastOrderId];
    setCursorHistory(nextHistory);
    setPageIndex(pageIndex + 1);
    await fetchPage(lastOrderId, filters);
  };

  const handlePrev = async () => {
    if (pageIndex === 0) return;
    const prevIndex = pageIndex - 1;
    setPageIndex(prevIndex);
    await fetchPage(cursorHistory[prevIndex], filters);
  };

  // 최초 진입 시 기본 기간(최근 7일)으로 한 번 조회
  React.useEffect(() => { fetchPage(null, filters); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

  const handleConfirmStatusChange = async () => {
    const { order, newStatus } = statusTarget;
    try {
      await updateOrderStatus(order.orderId, order.orderDate, newStatus, auth.adminName);
      setStatusTarget(null);
      await fetchPage(cursorHistory[pageIndex], filters);
    } catch (err) {
      pushToast(err.response?.data?.message || '주문 상태 변경에 실패했습니다.', 'error');
    }
  };

  return (
    <AdminLayout title="상품주문관리" menuUrl="/admin/order">
      <ToastStack toasts={toasts} onDismiss={removeToast} />

      <div className="page-header">
        <div>
          <h1>상품주문관리</h1>
          <div className="subtitle">
            대용량(1일 최대 천만건) 처리를 위해 페이지번호 대신 다음/이전 방식으로 조회합니다.
            조회 기간은 필수입니다.
          </div>
        </div>
      </div>

      <div className="list-toolbar">
        <form className="search-bar" onSubmit={handleSearch}>
          <input type="date" className="input" value={filters.searchStartDate}
            onChange={(e) => setFilters((f) => ({ ...f, searchStartDate: e.target.value }))} required />
          <span className="text-secondary">~</span>
          <input type="date" className="input" value={filters.searchEndDate}
            onChange={(e) => setFilters((f) => ({ ...f, searchEndDate: e.target.value }))} required />
          <select className="select" value={filters.orderStatus} onChange={(e) => setFilters((f) => ({ ...f, orderStatus: e.target.value }))}>
            {STATUS_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
          <input type="text" className="input keyword-input" placeholder="주문회원 계정으로 검색"
            value={filters.memberAccount} onChange={(e) => setFilters((f) => ({ ...f, memberAccount: e.target.value }))} />
          <button type="submit" className="btn btn-secondary">검색</button>
        </form>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>주문번호</th><th>주문일</th><th>회원</th><th>상품/옵션</th><th>수량</th><th>금액</th><th>상태</th><th>등록일시</th><th>관리</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} className="empty-row">불러오는 중...</td></tr>}
              {!loading && error && <tr><td colSpan={9} className="empty-row error-state">{error}</td></tr>}
              {!loading && !error && rows.length === 0 && <tr><td colSpan={9} className="empty-row">조회된 주문이 없습니다.</td></tr>}
              {!loading && !error && rows.map((o) => (
                <tr key={o.orderId}>
                  <td>{o.orderId}</td>
                  <td>{o.orderDate}</td>
                  <td>{o.memberAccount}</td>
                  <td>{o.productName} <span className="text-secondary">({o.optionName})</span></td>
                  <td>{o.orderQty}</td>
                  <td>{Number(o.orderAmount).toLocaleString()}원</td>
                  <td><StatusBadge value={o.orderStatus} label={STATUS_OPTIONS.find((s) => s.value === o.orderStatus)?.label} /></td>
                  <td>{formatDateTime(o.createdAt)}</td>
                  <td className="actions">
                    <select
                      className="select"
                      value=""
                      onChange={(e) => {
                        if (e.target.value) setStatusTarget({ order: o, newStatus: e.target.value });
                        e.target.value = '';
                      }}
                    >
                      <option value="">상태변경</option>
                      {STATUS_OPTIONS.filter((s) => s.value && s.value !== o.orderStatus).map((s) => (
                        <option key={s.value} value={s.value}>{s.label}로 변경</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="pagination">
        <button disabled={pageIndex === 0 || loading} onClick={handlePrev}>&lsaquo; 이전</button>
        <button disabled={!hasNext || loading} onClick={handleNext}>다음 &rsaquo;</button>
      </div>

      {statusTarget && (
        <ConfirmDialog
          title="주문 상태 변경"
          message={`주문번호 ${statusTarget.order.orderId} 를 '${STATUS_OPTIONS.find((s) => s.value === statusTarget.newStatus)?.label}' 상태로 변경하시겠습니까? 주문회원에게 알림이 발송됩니다.${statusTarget.newStatus === 'CANCELLED' ? ' (재고가 즉시 복원됩니다)' : ''}`}
          confirmLabel="변경"
          onCancel={() => setStatusTarget(null)}
          onConfirm={handleConfirmStatusChange}
        />
      )}
    </AdminLayout>
  );
}
